// Admin panel — generate SEO blog drafts via Claude. Type a topic, click
// generate, the post is saved as draft. List existing drafts + publish button.
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, FileText, CheckCircle2 } from "lucide-react";

export default function AdminBlogGenerator() {
  const [topic, setTopic] = useState("");
  const [lang, setLang] = useState("de");
  const [generating, setGenerating] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [lastResult, setLastResult] = useState(null);

  const loadDrafts = () => {
    api.get("/admin/blog/drafts").then((r) => setDrafts(r.data.drafts || [])).catch(() => {});
  };

  useEffect(() => { loadDrafts(); }, []);

  const generate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setLastResult(null);
    try {
      const r = await api.post("/admin/blog/generate", { topic: topic.trim(), lang }, { timeout: 90000 });
      setLastResult(r.data);
      toast.success("Entwurf erstellt!");
      setTopic("");
      loadDrafts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Generierung");
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (postId) => {
    try {
      await api.post(`/admin/blog/${postId}/publish`);
      toast.success("Veröffentlicht!");
      loadDrafts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div data-testid="admin-blog-generator">
      <h2 className="text-2xl font-black tracking-tight mb-6 flex items-center gap-2">
        <Sparkles size={18} className="text-[#FF5A00]" /> KI-Blog Generator
      </h2>

      <div className="bg-gradient-to-br from-orange-50 to-white border border-[#FF5A00]/30 p-5 mb-8">
        <div className="text-[11px] font-bold tracking-widest uppercase text-[#FF5A00] mb-2">Neuer Beitrag</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="z.B. '10 Tipps für einen reibungslosen Umzug in Hamburg'"
            className="flex-1 border border-neutral-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#FF5A00]"
            disabled={generating}
            data-testid="blog-topic-input"
          />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            disabled={generating}
            className="border border-neutral-300 px-3 py-2.5 text-sm bg-white"
            data-testid="blog-lang-select"
          >
            <option value="de">Deutsch</option>
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
          <button
            type="button"
            onClick={generate}
            disabled={generating || !topic.trim()}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide text-sm px-5 py-2.5 transition disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="blog-generate-btn"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {generating ? "Schreibt…" : "Generieren"}
          </button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">Powered by Claude Sonnet 4.5 · ~30–60 Sekunden · wird als Entwurf gespeichert.</p>
      </div>

      {lastResult && (
        <div className="bg-emerald-50 border border-emerald-200 p-4 mb-6" data-testid="blog-last-result">
          <div className="font-bold text-emerald-800 mb-1">{lastResult.title}</div>
          <div className="text-xs text-emerald-700 font-mono mb-2">Slug: /blog/{lastResult.slug}</div>
          <pre className="text-xs text-neutral-700 whitespace-pre-wrap leading-relaxed bg-white border border-emerald-100 p-3 max-h-40 overflow-y-auto">{lastResult.preview}…</pre>
        </div>
      )}

      <h3 className="text-sm font-bold tracking-widest uppercase text-neutral-500 mb-3">Entwürfe ({drafts.length})</h3>
      <div className="border border-neutral-200" data-testid="blog-drafts-list">
        {drafts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-neutral-500">Noch keine Entwürfe.</div>
        ) : (
          drafts.map((d) => (
            <div key={d.post_id} className="px-4 py-3 border-b border-neutral-100 last:border-0 flex items-center gap-3" data-testid={`blog-draft-${d.post_id}`}>
              <FileText size={14} className="text-neutral-400" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{d.title}</div>
                <div className="text-[11px] text-neutral-500 font-mono">/{d.slug} · {(d.created_at || "").slice(0, 10)}</div>
              </div>
              {d.ai_generated && <span className="text-[10px] font-bold text-[#FF5A00] uppercase">KI</span>}
              <button
                type="button"
                onClick={() => publish(d.post_id)}
                className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                data-testid={`blog-publish-${d.post_id}`}
              >
                <CheckCircle2 size={12} /> Veröffentlichen
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
